/**
 * PlatformDashboard — 6 KPI tiles (v2.1 §36 · P0 subset).
 * No graphs. No analytics. Just counts.
 */
import { useEffect, useState } from "react";
import { Building2, Zap, Sparkles, Users, Database, HeartPulse } from "lucide-react";
import api from "@/lib/api";

function formatBytes(n) {
  if (!n) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let i = 0;
  while (n >= 1024 && i < units.length - 1) {
    n /= 1024;
    i++;
  }
  return `${n.toFixed(1)} ${units[i]}`;
}

function Tile({ icon: Icon, label, value, tone = "slate", testid }) {
  const toneClass =
    tone === "green"
      ? "text-emerald-600"
      : tone === "amber"
        ? "text-amber-600"
        : tone === "red"
          ? "text-rose-600"
          : "text-slate-900";
  const iconBg =
    tone === "green"
      ? "bg-emerald-50"
      : tone === "amber"
        ? "bg-amber-50"
        : tone === "red"
          ? "bg-rose-50"
          : "bg-slate-100";
  return (
    <div
      className="rounded-2xl bg-white border border-slate-200 p-6 hover:border-slate-300 transition-colors shadow-sm"
      data-testid={testid}
    >
      <div className="flex items-center gap-3 mb-4">
        <div className={`w-10 h-10 rounded-lg ${iconBg} flex items-center justify-center`}>
          <Icon className={`w-5 h-5 ${toneClass}`} strokeWidth={1.75} />
        </div>
        <div className="text-[12px] uppercase tracking-[0.14em] text-slate-500 font-semibold">
          {label}
        </div>
      </div>
      <div className={`text-3xl font-semibold tabular-nums ${toneClass}`}>{value}</div>
    </div>
  );
}

export default function PlatformDashboard() {
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/platform/v1/dashboard");
        setKpis(data);
      } catch (e) {
        setErr(e?.response?.data?.detail || "Failed to load dashboard");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="text-slate-500" data-testid="platform-dashboard-loading">
        Loading…
      </div>
    );
  }

  if (err) {
    return (
      <div className="text-rose-600" data-testid="platform-dashboard-error">
        {err}
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="platform-dashboard">
      <header>
        <div className="text-[12px] uppercase tracking-[0.14em] text-amber-600 font-semibold">
          Platform
        </div>
        <h1 className="text-3xl font-semibold text-slate-900 mt-1">Dashboard</h1>
        <p className="text-slate-600 mt-2 text-sm">
          Health and usage of the HOCP platform itself — not hotel operations.
        </p>
      </header>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        <Tile
          icon={Building2}
          label="Total Hotels"
          value={kpis.hotels_total}
          testid="tile-hotels-total"
        />
        <Tile
          icon={Zap}
          label="Active Hotels"
          value={kpis.hotels_active}
          tone="green"
          testid="tile-hotels-active"
        />
        <Tile
          icon={Sparkles}
          label="Trial Hotels"
          value={kpis.hotels_trial}
          tone="amber"
          testid="tile-hotels-trial"
        />
        <Tile
          icon={Users}
          label="Total Employees"
          value={kpis.employees_total}
          testid="tile-employees-total"
        />
        <Tile
          icon={Database}
          label="Storage"
          value={formatBytes(kpis.storage_bytes)}
          testid="tile-storage"
        />
        <Tile
          icon={HeartPulse}
          label="Platform Health"
          value={kpis.platform_health === "ok" ? "OK" : kpis.platform_health}
          tone={kpis.platform_health === "ok" ? "green" : "red"}
          testid="tile-platform-health"
        />
      </div>
    </div>
  );
}
